import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Menu,
  Message,
  Modal,
} from 'semantic-ui-react';
import { API } from '../helpers';
import {
  ASR_FIRST_AUDIO_SEQUENCE,
  buildASRWebSocketURL,
  decodeServerFrame,
  encodeAudioFrame,
  encodeFullRequest,
  resamplePCM,
  shouldReportASRClose,
} from '../helpers/voiceAsrProtocol';
import {
  CLONE_MAX_RECORDING_SECONDS,
  CLONE_MIN_RECORDING_SECONDS,
  combineFloat32Chunks,
  encodeCloneWAV,
} from '../helpers/cloneRecorder';
import {
  isTerminalCloneStatus,
  formatVoiceTestError,
  validateCloneTest,
  validateTTSTest,
} from './voiceTestForm';

const formats = ['mp3', 'wav', 'ogg_opus', 'pcm'].map((value) => ({
  key: value,
  text: value,
  value,
}));

const initialTTS = {
  userId: '',
  input: '你好，这是一段语音合成测试。',
  voice: '',
  speed: 1,
  responseFormat: 'mp3',
};

const initialClone = { userId: '', referenceText: '', file: null };

const formatRecordingTime = (seconds) => {
  const wholeSeconds = Math.floor(seconds);
  return `${String(Math.floor(wholeSeconds / 60)).padStart(2, '0')}:${String(wholeSeconds % 60).padStart(2, '0')}`;
};

export default function ManagedVoiceTestModal({ open, channel, onClose }) {
  const [tab, setTab] = useState(channel?.type === 49 ? 'chat' : 'tts');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatResult, setChatResult] = useState('');
  const [tts, setTTS] = useState(initialTTS);
  const [ttsBusy, setTTSBusy] = useState(false);
  const [ttsError, setTTSError] = useState('');
  const [audioURL, setAudioURL] = useState('');
  const [asrText, setASRText] = useState('');
  const [asrError, setASRError] = useState('');
  const [asrBusy, setASRBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [clone, setClone] = useState(initialClone);
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [cloneResult, setCloneResult] = useState(null);
  const [cloneRecording, setCloneRecording] = useState(false);
  const [cloneElapsed, setCloneElapsed] = useState(0);
  const [cloneRecordingDuration, setCloneRecordingDuration] = useState(null);
  const [clonePreviewURL, setClonePreviewURL] = useState('');
  const socketRef = useRef(null);
  const asrCompletedRef = useRef(false);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const sequenceRef = useRef(ASR_FIRST_AUDIO_SEQUENCE);
  const sendQueueRef = useRef(Promise.resolve());
  const pollTimerRef = useRef(null);
  const pollingRef = useRef(false);
  const cloneStreamRef = useRef(null);
  const cloneAudioContextRef = useRef(null);
  const cloneSourceRef = useRef(null);
  const cloneProcessorRef = useRef(null);
  const cloneChunksRef = useRef([]);
  const cloneTimerRef = useRef(null);
  const cloneTimeoutRef = useRef(null);
  const cloneStartedAtRef = useRef(0);
  const clonePreviewURLRef = useRef('');
  const providerName = channel?.type === 49 ? '阿里百炼' : '豆包';
  const managedError = (error, fallback) =>
    formatVoiceTestError(error, fallback, providerName);

  const submitChat = async () => {
    setChatBusy(true);
    setChatError('');
    setChatResult('');
    try {
      const response = await API.get(`/api/channel/test/${channel.id}`, {
        params: { model: 'xingyu-chat' },
      });
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || '对话测试失败');
      }
      setChatResult(response.data.message || '对话模型响应正常');
    } catch (error) {
      setChatError(managedError(error, '对话测试失败'));
    } finally {
      setChatBusy(false);
    }
  };

  useEffect(() => {
    if (open) setTab(channel?.type === 49 ? 'chat' : 'tts');
  }, [open, channel?.id, channel?.type]);

  const cleanupASR = useCallback(() => {
    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (audioContextRef.current?.state !== 'closed') audioContextRef.current?.close();
    if (socketRef.current?.readyState < 2) socketRef.current.close();
    processorRef.current = null;
    sourceRef.current = null;
    streamRef.current = null;
    audioContextRef.current = null;
    socketRef.current = null;
    setRecording(false);
  }, []);

  const releaseCloneCapture = useCallback(() => {
    clearInterval(cloneTimerRef.current);
    clearTimeout(cloneTimeoutRef.current);
    cloneProcessorRef.current?.disconnect();
    cloneSourceRef.current?.disconnect();
    cloneStreamRef.current?.getTracks().forEach((track) => track.stop());
    if (cloneAudioContextRef.current?.state !== 'closed') cloneAudioContextRef.current?.close();
    cloneTimerRef.current = null;
    cloneTimeoutRef.current = null;
    cloneProcessorRef.current = null;
    cloneSourceRef.current = null;
    cloneStreamRef.current = null;
    cloneAudioContextRef.current = null;
    setCloneRecording(false);
  }, []);

  const clearClonePreview = useCallback(() => {
    if (clonePreviewURLRef.current) URL.revokeObjectURL(clonePreviewURLRef.current);
    clonePreviewURLRef.current = '';
    setClonePreviewURL('');
  }, []);

  const cleanupCloneRecorder = useCallback((clearResult = false) => {
    releaseCloneCapture();
    cloneChunksRef.current = [];
    if (clearResult) {
      clearClonePreview();
      setCloneElapsed(0);
      setCloneRecordingDuration(null);
      setClone((current) => ({ ...current, file: null }));
    }
  }, [clearClonePreview, releaseCloneCapture]);

  useEffect(() => {
    return () => {
      cleanupASR();
      cleanupCloneRecorder(true);
      pollingRef.current = false;
      clearTimeout(pollTimerRef.current);
    };
  }, [cleanupASR, cleanupCloneRecorder]);

  useEffect(() => {
    return () => {
      if (audioURL) URL.revokeObjectURL(audioURL);
    };
  }, [audioURL]);

  useEffect(() => {
    if (!open) {
      cleanupASR();
      cleanupCloneRecorder(true);
      pollingRef.current = false;
      clearTimeout(pollTimerRef.current);
    }
  }, [open, cleanupASR, cleanupCloneRecorder]);

  const submitTTS = async () => {
    const validation = validateTTSTest(tts);
    if (validation) { setTTSError(validation); return; }
    setTTSBusy(true);
    setTTSError('');
    try {
      const response = await API.post(
        `/api/channel/${channel.id}/voice-test/tts`,
        {
          model: 'xingyu-tts',
          input: tts.input,
          voice: tts.voice.trim(),
          speed: Number(tts.speed),
          response_format: tts.responseFormat,
        },
        {
          responseType: 'blob',
          headers: { 'X-Xingyu-User-ID': tts.userId.trim() || 'admin-voice-test' },
        }
      );
      if (!response) throw new Error('语音合成请求失败');
      setAudioURL(URL.createObjectURL(response.data));
    } catch (error) {
      setTTSError(managedError(error, '语音合成测试失败'));
    } finally {
      setTTSBusy(false);
    }
  };

  const attachSocketHandlers = (socket) => {
    socket.binaryType = 'arraybuffer';
    socket.onmessage = async (event) => {
      try {
        const result = await decodeServerFrame(event.data);
        if (result.error) throw new Error(`${result.error.code}: ${result.error.message}`);
        if (result.text) setASRText(result.text);
        if (result.final) { asrCompletedRef.current = true; setASRBusy(false); cleanupASR(); }
      } catch (error) {
        setASRError(managedError(error, '无法解析语音识别结果'));
        setASRBusy(false);
      }
    };
    socket.onerror = () => {
      setASRError('语音识别连接失败');
      setASRBusy(false);
    };
    socket.onclose = () => {
      if (shouldReportASRClose(socketRef.current === socket, asrCompletedRef.current)) {
        setASRError('语音识别连接已关闭，未返回最终结果');
        setASRBusy(false);
        cleanupASR();
      }
    };
  };

  const openASRSocket = () => new Promise((resolve, reject) => {
    cleanupASR();
    asrCompletedRef.current = false;
    const socket = new WebSocket(buildASRWebSocketURL(channel.id));
    socketRef.current = socket;
    attachSocketHandlers(socket);
    socket.onopen = async () => {
      try {
        socket.send(await encodeFullRequest({ user: { uid: 'oneapi-admin-test' }, request: { sequence: 1, show_utterances: true } }));
        resolve(socket);
      } catch (error) { reject(error); }
    };
  });

  const testASRFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setASRBusy(true);
    setASRError('');
    setASRText('');
    try {
      const socket = await openASRSocket();
      const context = new AudioContext();
      audioContextRef.current = context;
      const decoded = await context.decodeAudioData(await file.arrayBuffer());
      const pcm = resamplePCM(decoded.getChannelData(0), decoded.sampleRate);
      const chunkSize = 3200;
      let sequence = ASR_FIRST_AUDIO_SEQUENCE;
      for (let offset = 0; offset < pcm.length; offset += chunkSize) {
        const end = Math.min(offset + chunkSize, pcm.length);
        socket.send(await encodeAudioFrame(pcm.slice(offset, end), sequence, end === pcm.length));
        sequence += 1;
      }
    } catch (error) {
      setASRError(managedError(error, '音频文件识别失败'));
      setASRBusy(false);
      cleanupASR();
    } finally {
      event.target.value = '';
    }
  };

  const startMicrophone = async () => {
    setASRBusy(true);
    setASRError('');
    setASRText('');
    try {
      const socket = await openASRSocket();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } });
      const context = new AudioContext();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      streamRef.current = stream;
      audioContextRef.current = context;
      sourceRef.current = source;
      processorRef.current = processor;
      sequenceRef.current = ASR_FIRST_AUDIO_SEQUENCE;
      sendQueueRef.current = Promise.resolve();
      processor.onaudioprocess = (event) => {
        const pcm = resamplePCM(event.inputBuffer.getChannelData(0), context.sampleRate);
        const sequence = sequenceRef.current++;
        sendQueueRef.current = sendQueueRef.current.then(async () => {
          if (socket.readyState === WebSocket.OPEN) socket.send(await encodeAudioFrame(pcm, sequence));
        });
      };
      source.connect(processor);
      processor.connect(context.destination);
      setRecording(true);
    } catch (error) {
      setASRError(managedError(error, '无法启动麦克风识别'));
      setASRBusy(false);
      cleanupASR();
    }
  };

  const stopMicrophone = async () => {
    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    setRecording(false);
    const socket = socketRef.current;
    const sequence = sequenceRef.current++;
    await sendQueueRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(await encodeAudioFrame(new Int16Array(0), sequence, true));
  };

  const pollClone = async (voiceId, userId, attempt = 0) => {
    if (!pollingRef.current || attempt >= 60) {
      if (attempt >= 60) setCloneError('训练状态查询超时，请稍后重新查询');
      setCloneBusy(false);
      return;
    }
    try {
      const response = await API.get(`/api/channel/${channel.id}/voice-test/clone/${voiceId}`, { params: { user_id: userId } });
      if (!response) throw new Error('训练状态查询失败');
      setCloneResult(response.data);
      if (isTerminalCloneStatus(response.data.status)) {
        pollingRef.current = false;
        setCloneBusy(false);
        if (response.data.status === 'ready') {
          setTTS((current) => ({ ...current, voice: response.data.id, userId }));
        }
        if (response.data.status === 'failed') setCloneError(`${providerName}声音复刻训练失败`);
        return;
      }
      pollTimerRef.current = setTimeout(() => pollClone(voiceId, userId, attempt + 1), 2000);
    } catch (error) {
      setCloneError(managedError(error, '训练状态查询失败'));
      setCloneBusy(false);
      pollingRef.current = false;
    }
  };

  const stopCloneRecording = useCallback(() => {
    if (!cloneAudioContextRef.current) return;
    const sourceRate = cloneAudioContextRef.current.sampleRate;
    const maxSamples = Math.floor(sourceRate * CLONE_MAX_RECORDING_SECONDS);
    const samples = combineFloat32Chunks(cloneChunksRef.current).slice(0, maxSamples);
    releaseCloneCapture();
    cloneChunksRef.current = [];

    const recording = encodeCloneWAV([samples], sourceRate);
    setCloneElapsed(recording.durationSeconds);
    setCloneRecordingDuration(recording.durationSeconds);
    if (recording.durationSeconds < CLONE_MIN_RECORDING_SECONDS) {
      clearClonePreview();
      setClone((current) => ({ ...current, file: null }));
      setCloneError('录音时长必须在 10 到 20 秒之间');
      return;
    }

    const file = new File([recording.blob], 'managed-clone-recording.wav', { type: 'audio/wav' });
    clearClonePreview();
    const previewURL = URL.createObjectURL(recording.blob);
    clonePreviewURLRef.current = previewURL;
    setClonePreviewURL(previewURL);
    setClone((current) => ({ ...current, file }));
    setCloneError('');
  }, [clearClonePreview, releaseCloneCapture]);

  const startCloneRecording = async () => {
    if (cloneBusy || cloneRecording) return;
    cleanupCloneRecorder(true);
    setCloneError('');
    setCloneResult(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.AudioContext) {
        throw new Error('recording is not supported');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: false, noiseSuppression: false },
      });
      cloneStreamRef.current = stream;
      const context = new AudioContext();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      cloneAudioContextRef.current = context;
      cloneSourceRef.current = source;
      cloneProcessorRef.current = processor;
      cloneChunksRef.current = [];
      cloneStartedAtRef.current = Date.now();
      processor.onaudioprocess = (event) => {
        cloneChunksRef.current.push(new Float32Array(event.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(context.destination);
      setCloneRecording(true);
      setCloneElapsed(0);
      cloneTimerRef.current = setInterval(() => {
        setCloneElapsed(Math.min(
          (Date.now() - cloneStartedAtRef.current) / 1000,
          CLONE_MAX_RECORDING_SECONDS
        ));
      }, 200);
      cloneTimeoutRef.current = setTimeout(stopCloneRecording, CLONE_MAX_RECORDING_SECONDS * 1000);
    } catch (error) {
      releaseCloneCapture();
      setCloneError('无法启动麦克风录音');
    }
  };

  const selectCloneFile = (event) => {
    const file = event.target.files?.[0] || null;
    releaseCloneCapture();
    clearClonePreview();
    cloneChunksRef.current = [];
    setCloneElapsed(0);
    setCloneRecordingDuration(null);
    setClone((current) => ({ ...current, file }));
    setCloneError('');
  };

  const submitClone = async () => {
    if (cloneRecording) return;
    const validation = validateCloneTest({ ...clone, recordingDuration: cloneRecordingDuration });
    if (validation) { setCloneError(validation); return; }
    if (!window.confirm(`这会提交真实的${providerName}声音复刻训练并绑定到该业务用户，确认继续吗？`)) return;
    setCloneBusy(true);
    setCloneError('');
    setCloneResult(null);
    const form = new FormData();
    form.append('user_id', clone.userId.trim());
    form.append('reference_text', clone.referenceText.trim());
    form.append('audio', clone.file);
    try {
      const response = await API.post(`/api/channel/${channel.id}/voice-test/clone`, form);
      if (!response) throw new Error('声音复刻提交失败');
      setCloneResult(response.data);
      if (isTerminalCloneStatus(response.data.status)) {
        setCloneBusy(false);
        if (response.data.status === 'ready') {
          setTTS((current) => ({ ...current, voice: response.data.id, userId: clone.userId.trim() }));
        }
        return;
      }
      pollingRef.current = true;
      pollTimerRef.current = setTimeout(() => pollClone(response.data.id, clone.userId.trim()), 2000);
    } catch (error) {
      setCloneError(managedError(error, '声音复刻提交失败'));
      setCloneBusy(false);
    }
  };

  const selectTab = (nextTab) => {
    if (tab === 'clone' && nextTab !== 'clone') {
      releaseCloneCapture();
      cloneChunksRef.current = [];
      clearClonePreview();
      if (cloneRecording) {
        setCloneElapsed(0);
        setCloneRecordingDuration(null);
      }
    }
    setTab(nextTab);
  };

  return (
    <Modal open={open} onClose={onClose} closeIcon size='large' aria-label={`${providerName}渠道测试`}>
      <Modal.Header>{providerName}渠道测试 · {channel?.name || ''}</Modal.Header>
      <Modal.Content>
        <Menu pointing secondary widths={channel?.type === 49 ? 4 : 3}>
          {channel?.type === 49 && <Menu.Item name='对话模型' active={tab === 'chat'} onClick={() => selectTab('chat')} />}
          <Menu.Item name='ASR 语音识别' active={tab === 'asr'} onClick={() => selectTab('asr')} />
          <Menu.Item name='TTS 语音合成' active={tab === 'tts'} onClick={() => selectTab('tts')} />
          <Menu.Item name='声音复刻' active={tab === 'clone'} onClick={() => selectTab('clone')} />
        </Menu>

        {tab === 'chat' && <Form loading={chatBusy} error={Boolean(chatError)}>
          <Message info content='使用 xingyu-chat 别名测试 qwen-flash 对话响应。' />
          <Message error content={chatError} />
          {chatResult && <Message positive header='对话测试成功' content={chatResult} />}
          <Button primary icon labelPosition='left' onClick={submitChat}><Icon name='comments' />开始测试</Button>
        </Form>}

        {tab === 'tts' && <Form loading={ttsBusy} error={Boolean(ttsError)}>
          <Form.TextArea label='合成文本' value={tts.input} onChange={(_, data) => setTTS({ ...tts, input: data.value })} />
          <Form.Group widths='equal'>
            <Form.Input label='音色（留空使用默认音色）' value={tts.voice} onChange={(_, data) => setTTS({ ...tts, voice: data.value })} />
            <Form.Input label='业务用户 ID（复刻音色时填写）' value={tts.userId} onChange={(_, data) => setTTS({ ...tts, userId: data.value })} />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input label='语速' type='number' min='0.5' max='2' step='0.1' value={tts.speed} onChange={(_, data) => setTTS({ ...tts, speed: data.value })} />
            <Form.Field control={Dropdown} label='格式' selection options={formats} value={tts.responseFormat} onChange={(_, data) => setTTS({ ...tts, responseFormat: data.value })} />
          </Form.Group>
          <Message error content={ttsError} />
          {audioURL && <audio data-testid='tts-player' controls src={audioURL} style={{ width: '100%', marginBottom: 12 }} />}
          <Button primary icon labelPosition='left' onClick={submitTTS}><Icon name='play' />生成并试听</Button>
          {audioURL && <Button as='a' href={audioURL} download={`voice-test.${tts.responseFormat}`} icon labelPosition='left'><Icon name='download' />下载</Button>}
        </Form>}

        {tab === 'asr' && <Form loading={asrBusy && !recording} error={Boolean(asrError)}>
          <Form.Field>
            <label>本地音频</label>
            <input type='file' accept='audio/*' onChange={testASRFile} disabled={asrBusy} />
          </Form.Field>
          <Message error content={asrError} />
          {asrText && <Message positive header='识别结果' content={asrText} />}
          {!recording
            ? <Button primary icon labelPosition='left' onClick={startMicrophone} disabled={asrBusy}><Icon name='microphone' />开始录音</Button>
            : <Button negative icon labelPosition='left' onClick={stopMicrophone}><Icon name='stop' />停止录音</Button>}
        </Form>}

        {tab === 'clone' && <Form loading={cloneBusy} error={Boolean(cloneError)}>
          <Form.Input label='业务用户 ID' value={clone.userId} onChange={(_, data) => setClone({ ...clone, userId: data.value })} />
          <Form.TextArea label='参考文本' value={clone.referenceText} onChange={(_, data) => setClone({ ...clone, referenceText: data.value })} />
          <Form.Field>
            <label>WAV、MP3 或 M4A 录音文件</label>
            <input type='file' accept='.wav,.mp3,.m4a,audio/wav,audio/mpeg,audio/mp4' onChange={selectCloneFile} disabled={cloneBusy || cloneRecording} />
          </Form.Field>
          <Form.Field>
            <label>直接录音</label>
            <div style={{ marginBottom: 10 }}>{formatRecordingTime(cloneElapsed)} / 00:20</div>
            {!cloneRecording
              ? <Button type='button' primary icon labelPosition='left' onClick={startCloneRecording} disabled={cloneBusy}>
                <Icon name='microphone' />{clonePreviewURL ? '重新录音' : '开始录音'}
              </Button>
              : <Button type='button' negative icon labelPosition='left' onClick={stopCloneRecording}>
                <Icon name='stop' />停止录音
              </Button>}
          </Form.Field>
          {clonePreviewURL && <audio controls src={clonePreviewURL} style={{ width: '100%', marginBottom: 12 }} />}
          {clone.file && <Message info content={cloneRecordingDuration !== null
            ? `当前音频：浏览器录音（${cloneRecordingDuration.toFixed(1)} 秒）`
            : `当前音频：${clone.file.name}`} />}
          <Message warning content={`测试会产生${providerName}官方用量，训练成功后声音 ID 将绑定到该业务用户。`} />
          <Message error content={cloneError} />
          {cloneResult && <Message positive={cloneResult.status === 'ready'} header={`状态：${cloneResult.status}`} content={`声音 ID：${cloneResult.id}`} />}
          {cloneResult?.status === 'ready' && <Button type='button' positive icon labelPosition='left' onClick={() => selectTab('tts')}>
            <Icon name='volume up' />使用该音色试听
          </Button>}
          <Button primary icon labelPosition='left' onClick={submitClone} disabled={cloneBusy || cloneRecording}><Icon name='upload' />提交复刻训练</Button>
        </Form>}
      </Modal.Content>
      <Modal.Actions><Button onClick={onClose}>关闭</Button></Modal.Actions>
    </Modal>
  );
}
